// Considered: TypePerspectiveRowMolecule's open body and a stack of TypeFieldRowMolecule rows —
// rejected because the row's body is a four-control readback with no baseline to revert to, while
// the contract's `PerspEditor` (`type-perspectives.jsx:76-178`) is a per-field OVERRIDE surface
// (every field states what it would inherit and carries its own revert, the row carries a
// follow-the-ancestor-again lever) that is mounted TWICE — once inside an open row, once as the
// declare form — which a body nested inside the row molecule cannot be. The field SHAPE is still
// TypeFieldRowMolecule's, composed here through its `custom` control slot. See spec Reuse Audit.
import React from 'react';
import { Trans, useTranslation } from 'localization';
import type { SchemaPerspective } from 'types/schema';
import type { FilterExpression } from 'types/filters';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import TypeFieldRowMolecule from './TypeFieldRowMolecule';
import { assigneeSentence, sameAssigneeFilter, type PerspectiveAssigneeKind } from './assigneeSentence';
import './PerspectiveEditorMolecule.scss';

export type { PerspectiveAssigneeKind } from './assigneeSentence';

/** The fields this editor overlays. `filter` is `assignee_filter`; the rest are `SchemaPerspective`'s own. */
export type PerspectiveEditorField = 'name' | 'description' | 'cardinality' | 'filter' | 'permissions' | 'required';

/**
 * The contract datum ids for ONE call site. The prototype composes the same `PerspEditor` twice
 * — inside an open row and inside the declare form — and the decomposition gives each mounting
 * its own ids, so the ids are data rather than literals in the markup (the same seam
 * `relationshipScopeCopy` gives the two scope editors).
 */
export interface PerspectiveEditorParity {
  inheritedNotice: string;
  followAll: string;
  redeclareHint: string;
  nameField: string;
  nameRevert: string;
  nameHint: string;
  nameInput: string;
  descField: string;
  descRevert: string;
  descHint: string;
  descInput: string;
  cardField: string;
  cardRevert: string;
  cardHint: string;
  cardOption: string;
  assigneeField: string;
  assigneeRevert: string;
  assigneeHint: string;
  assigneeKind: string;
  resolvesTo: string;
  resolvesValue: string;
  anyone: string;
  permField: string;
  permRevert: string;
  permHint: string;
  permChip: string;
  requiredField: string;
  requiredCheckbox: string;
  requiredRevert: string;
  hiddenNote: string;
  globalNote: string;
}

/** The editor as an open row composes it (`persp > list > each(live) > <Row> > <PerspEditor>`). */
export const PERSPECTIVE_EDITOR_PARITY: PerspectiveEditorParity = {
  inheritedNotice: 'persp.declares-this',
  followAll: 'persp.follow',
  redeclareHint: 'persp.a-subtype-that-redeclares-this-one-overr',
  nameField: 'persp.children',
  nameRevert: 'persp.revert',
  nameHint: 'persp.hint',
  nameInput: 'persp.input',
  descField: 'persp.children#2',
  descRevert: 'persp.revert#2',
  descHint: 'persp.hint#2',
  descInput: 'persp.input#2',
  cardField: 'persp.children#3',
  cardRevert: 'persp.revert#3',
  cardHint: 'persp.hint#3',
  cardOption: 'persp.c',
  assigneeField: 'persp.children#4',
  assigneeRevert: 'persp.revert#4',
  assigneeHint: 'persp.hint#4',
  assigneeKind: 'persp.k-label',
  resolvesTo: 'persp.resolves-to',
  resolvesValue: 'persp.assigneesay-filter',
  anyone: 'persp.anyone',
  permField: 'persp.children#5',
  permRevert: 'persp.revert#5',
  permHint: 'persp.hint#5',
  permChip: 'persp.k',
  requiredField: 'persp.inheritednote-required',
  requiredCheckbox: 'persp.checkbox',
  requiredRevert: 'persp.revert#6',
  hiddenNote: 'persp.hidden-from-people-set-by-migration-not-',
  globalNote: 'persp.global-it-reaches-every-descendant-and-o',
};

/** The same editor as the declare form composes it (`persp > <PerspEditor>`). */
export const PERSPECTIVE_DECLARE_PARITY: PerspectiveEditorParity = {
  inheritedNotice: 'persp.declares-this#2',
  followAll: 'persp.follow#2',
  redeclareHint: 'persp.a-subtype-that-redeclares-this-one-overr#2',
  nameField: 'persp.children#6',
  nameRevert: 'persp.revert#7',
  nameHint: 'persp.hint#6',
  nameInput: 'persp.input#3',
  descField: 'persp.children#7',
  descRevert: 'persp.revert#8',
  descHint: 'persp.hint#7',
  descInput: 'persp.input#4',
  cardField: 'persp.children#8',
  cardRevert: 'persp.revert#9',
  cardHint: 'persp.hint#8',
  cardOption: 'persp.c#2',
  assigneeField: 'persp.children#9',
  assigneeRevert: 'persp.revert#10',
  assigneeHint: 'persp.hint#9',
  assigneeKind: 'persp.k-label#2',
  resolvesTo: 'persp.resolves-to#2',
  resolvesValue: 'persp.assigneesay-filter#2',
  anyone: 'persp.anyone#2',
  permField: 'persp.children#10',
  permRevert: 'persp.revert#11',
  permHint: 'persp.hint#10',
  permChip: 'persp.k#2',
  requiredField: 'persp.inheritednote-required#2',
  requiredCheckbox: 'persp.checkbox#2',
  requiredRevert: 'persp.revert#12',
  hiddenNote: 'persp.hidden-from-people-set-by-migration-not-#2',
  globalNote: 'persp.global-it-reaches-every-descendant-and-o#2',
};

/** The same editor as a "not on this type" row mounts it (`persp > list#2 > each(gone) > <Row> > <PerspEditor>`). */
export const PERSPECTIVE_GONE_ROW_EDITOR_PARITY: PerspectiveEditorParity = {
  inheritedNotice: 'persp.declares-this#3',
  followAll: 'persp.follow#3',
  redeclareHint: 'persp.a-subtype-that-redeclares-this-one-overr#3',
  nameField: 'persp.children#11',
  nameRevert: 'persp.revert#13',
  nameHint: 'persp.hint#11',
  nameInput: 'persp.input#5',
  descField: 'persp.children#12',
  descRevert: 'persp.revert#14',
  descHint: 'persp.hint#12',
  descInput: 'persp.input#6',
  cardField: 'persp.children#13',
  cardRevert: 'persp.revert#15',
  cardHint: 'persp.hint#13',
  cardOption: 'persp.c#3',
  assigneeField: 'persp.children#14',
  assigneeRevert: 'persp.revert#16',
  assigneeHint: 'persp.hint#14',
  assigneeKind: 'persp.k-label#3',
  resolvesTo: 'persp.resolves-to#3',
  resolvesValue: 'persp.assigneesay-filter#3',
  anyone: 'persp.anyone#3',
  permField: 'persp.children#15',
  permRevert: 'persp.revert#17',
  permHint: 'persp.hint#15',
  permChip: 'persp.k#3',
  requiredField: 'persp.inheritednote-required#3',
  requiredCheckbox: 'persp.checkbox#3',
  requiredRevert: 'persp.revert#18',
  hiddenNote: 'persp.hidden-from-people-set-by-migration-not-#3',
  globalNote: 'persp.global-it-reaches-every-descendant-and-o#3',
};

export interface PerspectiveEditorMoleculeProps {
  /**
   * What each field WOULD be with no overlay — the ancestor's definition for an inherited
   * perspective, this type's saved one otherwise. Never mutated: every change lands in `edits`,
   * which is what makes "revert" a real answer rather than a second guess.
   */
  base: SchemaPerspective;
  /** This session's field overlay. A key present here is changed; absent means "follows `base`". */
  edits?: Partial<SchemaPerspective>;
  /** The ancestor's resolved DISPLAY name (B7) — never the raw `schema_id` the wire carries. */
  inheritedFromName?: string;
  /** This type's display name, for the declare form's title and the rename warning. */
  typeName: string;
  /**
   * The permission words this editor may offer. Supplied by the caller rather than restated
   * here — a second list of permission words is how the two came to disagree in the first
   * place (`type-perspectives.jsx:28-30`). The composer passes the union observed across the
   * merged perspectives, which the schema read resolves from the SpiceDB schema.
   */
  permissionVocabulary: string[];
  /** The kinds of thing that may hold this standing, from the caller's own catalog. */
  assigneeKinds: PerspectiveAssigneeKind[];
  readOnly?: boolean;
  onEdit?: (field: PerspectiveEditorField, value: unknown) => void;
  /** `null` means "follow the ancestor on everything" — one lever for the whole row. */
  onRevert?: (field: PerspectiveEditorField | null) => void;
  /**
   * `'override'` (default) is the body an open row shows. `'declare'` adds the head that names
   * what is being authored and the Declare it / Cancel footer — the `p-new` wrapper the
   * contract puts around this same editor (`type-perspectives.jsx:352-378`).
   */
  variant?: 'override' | 'declare';
  onDeclare?: () => void;
  onCancel?: () => void;
  parity?: PerspectiveEditorParity;
  className?: string;
  'data-testid'?: string;
}

const CARDINALITIES: SchemaPerspective['cardinality'][] = ['single', 'multiple'];

/** The editor's field key → the `SchemaPerspective` key it overlays. */
const FIELD_KEY: Record<PerspectiveEditorField, keyof SchemaPerspective> = {
  name: 'label',
  description: 'description',
  cardinality: 'cardinality',
  filter: 'assignee_filter',
  permissions: 'permissions',
  required: 'required',
};

const PerspectiveEditorMolecule: React.FC<PerspectiveEditorMoleculeProps> = ({
  base,
  edits,
  inheritedFromName,
  typeName,
  permissionVocabulary,
  assigneeKinds,
  readOnly = false,
  onEdit,
  onRevert,
  variant = 'override',
  onDeclare,
  onCancel,
  parity = PERSPECTIVE_EDITOR_PARITY,
  className = '',
  'data-testid': dataTestId = 'perspective-editor',
}) => {
  const { t } = useTranslation();
  const from = inheritedFromName ?? t('studio.typeEditor.row.unknownAncestor');
  const record = base as unknown as Record<string, unknown>;
  const overlay = (edits ?? {}) as Record<string, unknown>;

  const val = (field: PerspectiveEditorField): unknown => {
    const key = FIELD_KEY[field] as string;
    return key in overlay ? overlay[key] : record[key];
  };
  const changed = (field: PerspectiveEditorField): boolean => {
    const key = FIELD_KEY[field] as string;
    return key in overlay && overlay[key] !== record[key];
  };
  const anyChange = (Object.keys(FIELD_KEY) as PerspectiveEditorField[]).some(changed);

  const name = (val('name') as string) ?? '';
  const description = (val('description') as string) ?? '';
  const cardinality = (val('cardinality') as SchemaPerspective['cardinality']) ?? 'single';
  const filter = (val('filter') as FilterExpression[]) ?? [];
  const permissions = (val('permissions') as string[]) ?? [];
  const required = !!val('required');
  const inherited = !!base.is_inherited;

  const say = (expression: FilterExpression) =>
    (assigneeKinds.find((kind) => sameAssigneeFilter(expression, kind))?.label ?? String(expression.value)).toLowerCase();

  // The readable form of the OR-list, said with the caller's own kind labels so the
  // sentence and the chips above it cannot drift apart — and the same sentence the closed
  // row carries, so opening a row never changes what it says.
  const resolvesTo = assigneeSentence(filter, assigneeKinds, t);

  /**
   * What a field would inherit, shown ON the field: an override you cannot see the baseline
   * for is not a decision, it is a guess (`type-perspectives.jsx:82-90`).
   */
  const wasNote = (field: PerspectiveEditorField): React.ReactNode => {
    if (!inherited || !changed(field)) return null;
    const was = record[FIELD_KEY[field] as string];
    let shown: string;
    if (field === 'filter') {
      const listed = ((was as FilterExpression[]) ?? []).map(say).join(', ');
      shown = listed || t('studio.typeEditor.perspectives.editor.anyone');
    } else if (Array.isArray(was)) {
      shown = was.join(', ');
    } else if (typeof was === 'boolean') {
      shown = t(was ? 'studio.typeEditor.advanced.yes' : 'studio.typeEditor.advanced.no');
    } else {
      shown = (was as string) || t('studio.typeEditor.perspectives.editor.nothing');
    }
    return (
      <span className="perspective-editor__was">
        <Trans i18nKey="studio.typeEditor.perspectives.editor.was" values={{ from, shown }} components={{ b: <b /> }} />
      </span>
    );
  };

  const revert = (field: PerspectiveEditorField, datum: string): React.ReactNode =>
    changed(field) && !readOnly ? (
      <ButtonAtom
        variant="text"
        size="small"
        icon="pi pi-replay"
        label={t('studio.typeEditor.perspectives.editor.revert')}
        title={t('studio.typeEditor.perspectives.editor.revertTitle', { from })}
        className="perspective-editor__revert"
        onClick={() => onRevert?.(field)}
        data-datum={datum}
        data-kind="button"
        data-layout="stacked"
        data-testid={`perspective-editor-revert-${field}`}
      />
    ) : null;

  const toggleKind = (kind: PerspectiveAssigneeKind) => {
    const on = filter.some((expression) => sameAssigneeFilter(expression, kind));
    onEdit?.(
      'filter',
      on
        ? filter.filter((expression) => !sameAssigneeFilter(expression, kind))
        : [...filter, { property: kind.property, operator: 'equals', value: kind.value } as unknown as FilterExpression]
    );
  };

  const togglePermission = (word: string) =>
    onEdit?.('permissions', permissions.includes(word) ? permissions.filter((p) => p !== word) : [...permissions, word]);

  const declareBlocked = !name.trim() ? 'name' : permissions.length === 0 ? 'permissions' : null;
  const fieldMode = readOnly ? 'disabled' : 'edit';

  const editor = (
    <div className={['perspective-editor', className].filter(Boolean).join(' ')} data-testid={dataTestId}>
      {inherited && (
        <div className="perspective-editor__inherited" data-testid="perspective-editor-inherited">
          <i className="pi pi-sitemap" aria-hidden="true" />
          <span className="perspective-editor__inherited-text">
            <strong data-datum={parity.inheritedNotice} data-kind="strong">
              {t('studio.typeEditor.perspectives.editor.declaresThis', { from })}
            </strong>{' '}
            {t('studio.typeEditor.perspectives.editor.overlaysNote', { from })}
            {base.global ? ` ${t('studio.typeEditor.perspectives.editor.globalCannotBeRefused')}` : ''}
          </span>
          {anyChange && !readOnly && (
            <ButtonAtom
              variant="text"
              size="small"
              icon="pi pi-replay"
              label={t('studio.typeEditor.perspectives.editor.followAll', { from })}
              onClick={() => onRevert?.(null)}
              data-datum={parity.followAll}
              data-kind="button"
              data-testid="perspective-editor-follow-all"
            />
          )}
        </div>
      )}

      <p className="perspective-editor__lead" data-datum={parity.redeclareHint} data-kind="hint">
        {t('studio.typeEditor.perspectives.editor.redeclareNote')}
      </p>

      {/*
        Renaming an inherited perspective moves its translation keys to THIS type, so the
        ancestor's translations stop following it. Said where it happens.
      */}
      {inherited && changed('name') && (
        <div className="perspective-editor__rename-warning" role="note" data-testid="perspective-editor-rename-warning">
          <i className="pi pi-language" aria-hidden="true" />
          <span>{t('studio.typeEditor.perspectives.editor.renameWarning', { from, type: typeName })}</span>
        </div>
      )}

      <div className="perspective-editor__grid" data-container="list">
        <TypeFieldRowMolecule
          layout="stacked"
          kind="field"
          datum={parity.nameField}
          mode={fieldMode}
          label={t('studio.typeEditor.perspectives.editor.labelField')}
          afterLabel={revert('name', parity.nameRevert)}
          hint={
            inherited
              ? t('studio.typeEditor.perspectives.editor.labelHintInherited', { from, name: base.label || base.name })
              : t('studio.typeEditor.perspectives.editor.labelHint')
          }
          hintDatum={parity.nameHint}
          trailing={wasNote('name')}
          controls={[
            {
              kind: 'text',
              value: name,
              ariaLabel: t('studio.typeEditor.perspectives.editor.labelField'),
              datum: parity.nameInput,
              datumKind: 'input',
              grow: true,
              onChange: (value) => onEdit?.('name', value),
            },
          ]}
        />
        <TypeFieldRowMolecule
          layout="stacked"
          kind="field"
          datum={parity.descField}
          mode={fieldMode}
          label={t('studio.typeEditor.perspectives.editor.descriptionField')}
          afterLabel={revert('description', parity.descRevert)}
          hint={t('studio.typeEditor.perspectives.editor.descriptionHint')}
          hintDatum={parity.descHint}
          trailing={wasNote('description')}
          controls={[
            {
              kind: 'text',
              value: description,
              ariaLabel: t('studio.typeEditor.perspectives.editor.descriptionField'),
              datum: parity.descInput,
              datumKind: 'input',
              grow: true,
              onChange: (value) => onEdit?.('description', value),
            },
          ]}
        />
        <TypeFieldRowMolecule
          layout="stacked"
          kind="field"
          datum={parity.cardField}
          mode={fieldMode}
          label={t('studio.typeEditor.perspectives.editor.assigneesField')}
          afterLabel={revert('cardinality', parity.cardRevert)}
          hint={t('studio.typeEditor.perspectives.editor.assigneesFieldHint')}
          hintDatum={parity.cardHint}
          trailing={wasNote('cardinality')}
          controls={[
            {
              kind: 'custom',
              ariaLabel: t('studio.typeEditor.perspectives.cardinalityAria'),
              node: (
                <SegmentedControlAtom
                  mode="single"
                  size="small"
                  renderAs="button"
                  optionKind="button"
                  ariaLabel={t('studio.typeEditor.perspectives.cardinalityAria')}
                  value={cardinality}
                  options={CARDINALITIES.map((value) => ({
                    value,
                    label: t(`studio.typeEditor.perspectives.cardinality.${value}`),
                    datum: parity.cardOption,
                    disabled: readOnly,
                  }))}
                  onChange={(value) => onEdit?.('cardinality', value)}
                  data-testid="perspective-editor-cardinality"
                />
              ),
            },
          ]}
        />
      </div>

      <TypeFieldRowMolecule
        layout="stacked"
        kind="field"
        datum={parity.assigneeField}
        mode={fieldMode}
        label={t('studio.typeEditor.perspectives.editor.whoMayHoldIt')}
        afterLabel={revert('filter', parity.assigneeRevert)}
        hint={t('studio.typeEditor.perspectives.editor.whoMayHoldItHint')}
        hintDatum={parity.assigneeHint}
        trailing={wasNote('filter')}
        controls={[
          {
            kind: 'custom',
            ariaLabel: t('studio.typeEditor.perspectives.editor.whoMayHoldIt'),
            node: (
              <div className="perspective-editor__assignee">
                <div
                  className="perspective-editor__assignee-chips"
                  data-container="chips"
                  role="group"
                  aria-label={t('studio.typeEditor.perspectives.editor.whoMayHoldIt')}
                >
                  {assigneeKinds.map((kind) => {
                    const on = filter.some((expression) => sameAssigneeFilter(expression, kind));
                    return (
                      <button
                        key={`${kind.property}:${kind.value}`}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        className="perspective-editor__kind"
                        data-container="chips > each(kinds)"
                        data-on={on ? '1' : undefined}
                        disabled={readOnly}
                        title={kind.note ?? `${kind.property} = ${kind.value}`}
                        data-datum={parity.assigneeKind}
                        data-kind="button"
                        onClick={() => toggleKind(kind)}
                      >
                        {kind.label}
                      </button>
                    );
                  })}
                </div>
                <span className="perspective-editor__resolves">
                  {filter.length > 0 ? (
                    <>
                      <span data-datum={parity.resolvesTo} data-kind="hint">
                        {t('studio.typeEditor.perspectives.editor.resolvesTo')}
                      </span>{' '}
                      <strong data-datum={parity.resolvesValue} data-kind="strong">
                        {resolvesTo}
                      </strong>{' '}
                      {t('studio.typeEditor.perspectives.editor.anyOneQualifies')}
                    </>
                  ) : (
                    <>
                      {t('studio.typeEditor.perspectives.editor.nothingTicked')}{' '}
                      <strong data-datum={parity.anyone} data-kind="strong">
                        {t('studio.typeEditor.perspectives.editor.anyone')}
                      </strong>{' '}
                      {t('studio.typeEditor.perspectives.editor.narrowIt')}
                    </>
                  )}
                </span>
              </div>
            ),
          },
        ]}
      />

      <TypeFieldRowMolecule
        layout="stacked"
        kind="field"
        datum={parity.permField}
        mode={fieldMode}
        label={t('studio.typeEditor.perspectives.editor.holdingThisGrants')}
        afterLabel={revert('permissions', parity.permRevert)}
        hint={t('studio.typeEditor.perspectives.editor.holdingThisGrantsHint')}
        hintDatum={parity.permHint}
        trailing={wasNote('permissions')}
        controls={[
          {
            kind: 'custom',
            ariaLabel: t('studio.typeEditor.perspectives.editor.holdingThisGrants'),
            node: (
              <span
                className="perspective-editor__perms"
                data-container="chips"
                role="group"
                aria-label={t('studio.typeEditor.perspectives.editor.holdingThisGrants')}
              >
                {permissionVocabulary.map((word) => {
                  const on = permissions.includes(word);
                  return (
                    <button
                      key={word}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      className="perspective-editor__perm"
                      data-container="chips > each(permissions)"
                      data-on={on ? '1' : undefined}
                      disabled={readOnly}
                      data-datum={parity.permChip}
                      data-kind="chip"
                      onClick={() => togglePermission(word)}
                    >
                      {word}
                    </button>
                  );
                })}
              </span>
            ),
          },
        ]}
      />

      <div className="perspective-editor__flags">
        <span
          className="perspective-editor__flag"
          data-changed={changed('required') ? '1' : undefined}
          data-datum={parity.requiredField}
          data-kind="checkbox"
          data-layout="stacked"
        >
          <label className="perspective-editor__check">
            <input
              type="checkbox"
              checked={required}
              disabled={readOnly}
              data-datum={parity.requiredCheckbox}
              data-kind="checkbox"
              data-layout="stacked"
              data-testid="perspective-editor-required"
              onChange={(e) => onEdit?.('required', e.target.checked)}
            />
            {t('studio.typeEditor.perspectives.editor.requiredOnEverySphere')}
          </label>
          {revert('required', parity.requiredRevert)}
          {wasNote('required')}
        </span>
        {/*
          `is_hidden` is migration territory: flipping it changes what every existing UI shows
          without changing what anything means. Shown here, never set here.
        */}
        {base.is_hidden && (
          <span className="perspective-editor__flagnote" data-locked="1" data-datum={parity.hiddenNote} data-kind="hint">
            <i className="pi pi-eye-slash" aria-hidden="true" />
            {t('studio.typeEditor.perspectives.editor.hiddenNote')}
          </span>
        )}
        {base.global && (
          <span className="perspective-editor__flagnote" data-datum={parity.globalNote} data-kind="hint">
            <i className="pi pi-globe" aria-hidden="true" />
            {t('studio.typeEditor.perspectives.editor.globalNote')}
          </span>
        )}
      </div>
    </div>
  );

  if (variant === 'override') return editor;

  return (
    <div className="perspective-editor__new" data-testid="perspective-declare-form">
      <div className="perspective-editor__new-head">
        <span className="perspective-editor__new-glyph" data-datum="persp.chip" data-kind="chip">
          <i className="pi pi-user-plus" aria-hidden="true" />
        </span>
        <strong data-datum="persp.a-new-perspective-on" data-kind="strong">
          {t('studio.typeEditor.perspectives.editor.aNewPerspectiveOn', { type: typeName })}
        </strong>
      </div>
      {editor}
      <div className="perspective-editor__new-acts">
        <ButtonAtom
          variant="primary"
          size="small"
          label={t('studio.typeEditor.perspectives.editor.declareIt')}
          disabled={!!declareBlocked}
          onClick={onDeclare}
          data-datum="persp.declare-it"
          data-kind="button"
          data-testid="perspective-declare-submit"
        />
        <ButtonAtom
          variant="secondary"
          size="small"
          label={t('studio.typeEditor.perspectives.editor.cancel')}
          onClick={onCancel}
          data-datum="persp.cancel"
          data-kind="button"
          data-testid="perspective-declare-cancel"
        />
        {/*
          The held reason is stated beside the held action, not behind it (ux-craft I2): a
          disabled Declare it that does not say what is missing is a dead end.
        */}
        <span className="perspective-editor__new-hint" data-datum="persp.it-needs-a-name" data-kind="hint">
          {declareBlocked === 'name' && t('studio.typeEditor.perspectives.editor.itNeedsAName')}
          {declareBlocked === 'permissions' && t('studio.typeEditor.perspectives.editor.grantsNothing')}
          {declareBlocked === null && t('studio.typeEditor.perspectives.editor.readyToDeclare')}
        </span>
      </div>
    </div>
  );
};

PerspectiveEditorMolecule.displayName = 'PerspectiveEditorMolecule';

export default PerspectiveEditorMolecule;
